import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, CheckCircle2 } from "lucide-react";

interface BAAgreementProps {
  onAccept: () => void;
  isLoading?: boolean;
  accepted?: boolean;
}

const BAA_TEXT = `BUSINESS ASSOCIATE AGREEMENT

This Business Associate Agreement ("Agreement") is entered into between Elara Health, LLC ("Covered Entity") and the undersigned healthcare provider ("Business Associate").

1. DEFINITIONS
Terms used but not otherwise defined in this Agreement shall have the same meaning as those terms in the HIPAA Rules (45 CFR Parts 160 and 164).

2. OBLIGATIONS OF BUSINESS ASSOCIATE
Business Associate agrees to:
a) Not use or disclose Protected Health Information (PHI) other than as permitted or required by this Agreement or as required by law.
b) Use appropriate safeguards and comply with the Security Rule to prevent unauthorized use or disclosure of electronic PHI.
c) Report to Covered Entity any use or disclosure of PHI not provided for by this Agreement of which it becomes aware, including breaches of unsecured PHI.
d) Ensure that any subcontractors agree to the same restrictions and conditions that apply to Business Associate under this Agreement.
e) Make PHI available to individuals as required by the HIPAA Privacy Rule.
f) Make its internal practices, books, and records relating to the use and disclosure of PHI available to the Secretary of HHS for purposes of determining compliance.

3. PERMITTED USES AND DISCLOSURES
Business Associate may use or disclose PHI:
a) To perform functions, activities, or services for, or on behalf of, Covered Entity as specified in the provider service agreement.
b) For the proper management and administration of Business Associate or to carry out legal responsibilities.
c) For data aggregation services as permitted by the HIPAA Rules.

4. TERM AND TERMINATION
a) This Agreement is effective upon electronic acceptance and continues until terminated.
b) Covered Entity may terminate this Agreement if Business Associate violates a material term.
c) Upon termination, Business Associate shall return or destroy all PHI received from Covered Entity.

5. OBLIGATIONS OF COVERED ENTITY
Covered Entity shall:
a) Notify Business Associate of any limitations in its notice of privacy practices that may affect Business Associate's use of PHI.
b) Notify Business Associate of any changes in, or revocation of, authorization by an individual.

6. MISCELLANEOUS
a) This Agreement shall be governed by and construed in accordance with applicable federal law.
b) The Parties agree to take such action as is necessary to amend this Agreement from time to time as necessary for compliance with HIPAA Rules.

By electronically signing below, I acknowledge that I have read, understand, and agree to be bound by the terms of this Business Associate Agreement.`;

const BAAgreement = ({ onAccept, isLoading, accepted }: BAAgreementProps) => {
  const [hasRead, setHasRead] = useState(false);
  const [agreed, setAgreed] = useState(false);

  if (accepted) {
    return (
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-6 flex items-center gap-3">
          <CheckCircle2 size={20} className="text-primary shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">Business Associate Agreement Signed</p>
            <p className="text-xs text-muted-foreground">You have accepted the BAA.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-primary" />
          <h3 className="font-display text-lg text-foreground">Business Associate Agreement</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          As a HIPAA-covered entity, we require all providers to sign a Business Associate Agreement before accessing patient information.
        </p>

        <ScrollArea className="h-64 rounded-lg border border-border bg-muted/30 p-4">
          <pre className="whitespace-pre-wrap text-xs text-foreground font-sans leading-relaxed">
            {BAA_TEXT}
          </pre>
        </ScrollArea>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="baa-read"
              checked={hasRead}
              onCheckedChange={(v) => setHasRead(v === true)}
            />
            <label htmlFor="baa-read" className="text-sm text-foreground cursor-pointer">
              I have read the entire Business Associate Agreement
            </label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="baa-agree"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(v === true)}
              disabled={!hasRead}
            />
            <label htmlFor="baa-agree" className={`text-sm cursor-pointer ${!hasRead ? "text-muted-foreground" : "text-foreground"}`}>
              I agree to the terms and conditions of this BAA
            </label>
          </div>
        </div>

        <Button
          onClick={onAccept}
          disabled={!hasRead || !agreed || isLoading}
          className="w-full"
        >
          {isLoading ? "Signing..." : "Sign Business Associate Agreement"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default BAAgreement;
